import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from 'src/@/components/ui/badge';
import { Button } from 'src/@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/@/components/ui/select';
import useAuth from 'src/hooks/useAuth';
import Iconify from 'src/components/Iconify';
import { transformCategoryTemplateWhatsApp } from 'src/utils/meta.utils';
import actionListaCanaisPorCliente from 'src/actions/clientes/actionListaCanaisPorCliente';
import v1_actionWboTemplatesCategoryLogs from 'src/actions/v1/meta/wbo/v1_actionWboTemplatesCategoryLogs';
import v1_actionWboTemplatesCategoryLogsMarcarVistos from 'src/actions/v1/meta/wbo/v1_actionWboTemplatesCategoryLogsMarcarVistos';
import GenericTable from '../../../../components/table/GenericTable';
import { GENERIC_TABLE_DESIGN } from '../../../../components/table/GenericTable.design';

export default function TemplatesCategoryLogs() {
  const navigate = useNavigate();
  const { user, cliente } = useAuth();
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [canais, setCanais] = useState([]);
  const [canalFiltro, setCanalFiltro] = useState('all');
  const [clienteId, setClienteId] = useState('');
  const [pagination, setPagination] = useState({ current: 1, pages: 1, total: 0 });

  useEffect(() => {
    buscarCanais();
  }, []);

  useEffect(() => {
    if (clienteId) {
      buscarLogs(1, clienteId);
    }
  }, [clienteId, canalFiltro]);

  const buscarCanais = async () => {
    try {
      const id = cliente?._id || user?.clienteId;
      if (!id) return;

      const response = await actionListaCanaisPorCliente(id);
      const listaCanais = response?.data?.data || response?.data?.canais || [];
      const canaisWb = listaCanais.filter(c => c.platform === 'whatsapp_business');
      setCanais(canaisWb);

      // Marcar logs como vistos ao entrar na página
      v1_actionWboTemplatesCategoryLogsMarcarVistos(id).catch(() => {});

      if (canaisWb.length > 0) {
        const cId = canaisWb[0].cliente_id;
        setClienteId(cId);
        buscarLogs(1, cId);
      }
    } catch (err) {
      console.error('Erro ao buscar canais:', err);
    }
  };

  const buscarLogs = async (page = 1, cId = null) => {
    const idToUse = cId || clienteId;
    if (!idToUse) return;
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (canalFiltro !== 'all') params.canalId = canalFiltro;

      const res = await v1_actionWboTemplatesCategoryLogs(idToUse, params);
      const data = res.data?.data;
      setLogs(data?.logs || []);
      setPagination(data?.pagination || { current: 1, pages: 1, total: 0 });
    } catch (err) {
      console.error('Erro ao buscar logs:', err);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const categoryBadge = (category) => {
    const colors = {
      MARKETING: 'bg-purple-100 text-purple-700 border-purple-200',
      UTILITY: 'bg-blue-100 text-blue-700 border-blue-200',
      AUTHENTICATION: 'bg-amber-100 text-amber-700 border-amber-200',
    };
    return (
      <Badge variant="outline" className={`text-xs font-medium ${colors[category] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>
        {transformCategoryTemplateWhatsApp(category) || category}
      </Badge>
    );
  };

  return (
    <div className="flex flex-col h-full w-full gap-6 p-2 md:p-0 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/dashboard/configuracoes/templates-whatsapp')}
            className="p-2 rounded-xl hover:bg-gray-100 transition-colors text-gray-500"
          >
            <Iconify icon="eva:arrow-back-outline" width={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
              Logs de Categoria
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Historico de alteracoes de categoria dos templates pela Meta
            </p>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="w-64">
          <Select value={canalFiltro} onValueChange={setCanalFiltro}>
            <SelectTrigger className="h-10 rounded-xl border-gray-200 text-sm">
              <SelectValue placeholder="Todos os canais" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os canais</SelectItem>
              {canais.map((canal) => (
                <SelectItem key={canal._id} value={canal._id}>
                  {canal.nome || canal.sessao}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Button
          variant="outline"
          size="sm"
          className="rounded-xl"
          onClick={() => buscarLogs(1)}
          disabled={loading}
        >
          <Iconify icon="eva:refresh-outline" width={16} className="mr-1" />
          Atualizar
        </Button>

        {pagination.total > 0 && (
          <span className="text-xs text-gray-500 ml-auto">
            {pagination.total} registro{pagination.total !== 1 ? 's' : ''} encontrado{pagination.total !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Loading */}
      {loading && (
        <div className="w-full h-1 bg-gray-100 rounded-full overflow-hidden">
          <div className="h-full bg-[#7C3AED] rounded-full animate-pulse w-2/3" />
        </div>
      )}

      {/* Tabela */}
      {logs.length > 0 ? (
        <GenericTable
          headLabels={[
            { id: 'date', label: 'Data', className: 'w-44 pl-4' },
            { id: 'template', label: 'Template', className: 'flex-1' },
            { id: 'from', label: 'Categoria Anterior', className: 'w-44' },
            { id: 'arrow', label: '', className: 'w-10 text-center' },
            { id: 'to', label: 'Categoria Nova', className: 'w-44' },
            { id: 'channel', label: 'Canal', className: 'w-44 pr-4' },
          ]}
        >
          {logs.map((log) => (
            <div key={log._id} className={GENERIC_TABLE_DESIGN.row.container}>
              {/* Data */}
              <div className="w-44 pl-4 text-xs text-gray-600">
                {formatDate(log.createdAt)}
              </div>

              {/* Template */}
              <div className="flex-1 min-w-[200px] pr-4">
                <h3 className="text-sm font-semibold text-gray-900">
                  {log.templateName || log.templateId?.templateName || '-'}
                </h3>
                <span className="text-[11px] text-gray-400 font-mono">
                  {log.metaTemplateId || ''}
                </span>
              </div>

              {/* Categoria Anterior */}
              <div className="w-44">
                {categoryBadge(log.categoryAnterior)}
              </div>

              {/* Seta */}
              <div className="w-10 flex justify-center text-gray-400">
                <Iconify icon="eva:arrow-forward-outline" width={18} />
              </div>

              {/* Categoria Nova */}
              <div className="w-44">
                {categoryBadge(log.categoryNova)}
              </div>

              {/* Canal */}
              <div className="w-44 pr-4 text-sm text-gray-600 truncate">
                {log.canalId?.nome || '-'}
              </div>
            </div>
          ))}
        </GenericTable>
      ) : (
        !loading && (
          <div className="flex flex-col items-center justify-center p-12 bg-white rounded-xl border border-gray-100 border-dashed text-center">
            <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4 text-gray-400">
              <Iconify icon="eva:file-text-outline" width={28} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Nenhum log encontrado</h3>
            <p className="text-gray-500 max-w-sm">
              Nenhuma alteracao de categoria foi registrada ainda. Os logs serao criados automaticamente quando a Meta alterar a categoria de um template.
            </p>
          </div>
        )
      )}

      {/* Paginacao */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-center gap-2 pb-4">
          <Button
            variant="outline"
            size="sm"
            className="rounded-xl"
            disabled={pagination.current <= 1}
            onClick={() => buscarLogs(pagination.current - 1)}
          >
            <Iconify icon="eva:arrow-back-outline" width={16} />
          </Button>
          <span className="text-sm text-gray-600">
            Pagina {pagination.current} de {pagination.pages}
          </span>
          <Button
            variant="outline"
            size="sm"
            className="rounded-xl"
            disabled={pagination.current >= pagination.pages}
            onClick={() => buscarLogs(pagination.current + 1)}
          >
            <Iconify icon="eva:arrow-forward-outline" width={16} />
          </Button>
        </div>
      )}
    </div>
  );
}
